import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Alert, CircularProgress, Avatar, Button, Divider, Tooltip,
} from '@mui/material';
import { AccountBalance, FileDownload, Receipt } from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

async function downloadCsv(runId: string, type: 'detail' | 'summary', name: string) {
  const res = await fetch(`${API}/api/payroll-journal/${runId}/export?type=${type}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

const STATUS_COLORS: Record<string, string> = {
  approved: '#2e7d32', locked: '#1565c0', draft: '#546e7a', reviewing: '#f57f17',
};

const ACCOUNT_COLORS: Record<string, string> = {
  '1': '#1565c0', // Assets
  '2': '#6a1b9a', // Liabilities
  '5': '#b71c1c', // Expenses
};

const accountColor = (code: string) => ACCOUNT_COLORS[code[0]] ?? '#546e7a';

const fmt = (n: number, ccy = 'HKD') =>
  `${ccy} ${n.toLocaleString('en-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ITEM_TYPE_LABELS: Record<string, string> = {
  salary: 'Salary', hourly_wages: 'Wages', bonus: 'Bonus', overtime: 'Overtime',
  commission: 'Commission', allowance: 'Allowance',
  benefit_contribution_employer: 'Employer MPF/Benefits',
  benefit_contribution_employee: 'Employee MPF',
  tax_deduction: 'Tax Deduction', unpaid_leave: 'Unpaid Leave',
  other_deduction: 'Other Deduction', other_earning: 'Other Earning', net_pay: 'Net Pay',
};

export default function PayrollJournal() {
  const [runs, setRuns]         = useState<any[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [journal, setJournal]   = useState<any>(null);
  const [tab, setTab]           = useState(0);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [loadingJournal, setLoadingJournal] = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    apiFetch('/api/payroll-journal/runs')
      .then(r => setRuns(r ?? []))
      .catch(e => setError(e.message))
      .finally(() => setLoadingRuns(false));
  }, []);

  const loadJournal = useCallback(async (runId: string) => {
    setLoadingJournal(true);
    setJournal(null);
    try {
      const j = await apiFetch(`/api/payroll-journal/${runId}`);
      setJournal(j);
    } catch (e: any) { setError(e.message); }
    finally { setLoadingJournal(false); }
  }, []);

  useEffect(() => {
    if (selected) loadJournal(selected);
  }, [selected, loadJournal]);

  const debitTotal  = journal?.lines?.filter((l: any) => l.side === 'debit').reduce((s: number, l: any) => s + l.amount, 0) ?? 0;
  const creditTotal = journal?.lines?.filter((l: any) => l.side === 'credit').reduce((s: number, l: any) => s + l.amount, 0) ?? 0;
  const balanced    = Math.abs(debitTotal - creditTotal) < 0.01;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ bgcolor: '#1b5e20', width: 48, height: 48 }}><AccountBalance /></Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>Payroll Journal Export</Typography>
          <Typography variant="body2" color="text.secondary">
            Generate double-entry accounting journal entries from approved payroll runs
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Left: run list */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 0, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: '#1b5e20', color: '#fff' }}>
              <Typography variant="subtitle2" fontWeight={700}>Approved Payroll Runs</Typography>
            </Box>
            {loadingRuns ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : runs.length === 0 ? (
              <Box sx={{ p: 3, color: 'text.secondary', textAlign: 'center' }}>
                <Typography variant="body2">No approved payroll runs found.</Typography>
                <Typography variant="caption">Approve a payroll run first to generate a journal.</Typography>
              </Box>
            ) : (
              runs.map(r => (
                <Box key={r.id}
                  onClick={() => setSelected(r.id)}
                  sx={{
                    p: 2, cursor: 'pointer', borderBottom: '1px solid #f5f5f5',
                    bgcolor: selected === r.id ? '#e8f5e9' : 'transparent',
                    borderLeft: selected === r.id ? '4px solid #2e7d32' : '4px solid transparent',
                    '&:hover': { bgcolor: '#f9fbe7' },
                  }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={700}>{r.periodName}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.entityName}</Typography>
                    </Box>
                    <Chip size="small" label={r.status}
                      sx={{ bgcolor: STATUS_COLORS[r.status] + '20', color: STATUS_COLORS[r.status], fontSize: '0.6rem' }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Pay: {r.payDate?.slice(0, 10)}</Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {r.currency} {parseFloat(r.totalGross || 0).toLocaleString('en-HK', { minimumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        {/* Right: journal detail */}
        <Grid size={{ xs: 12, md: 8 }}>
          {!selected && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: 'text.secondary' }}>
              <Receipt sx={{ fontSize: 64, opacity: 0.2, mb: 1 }} />
              <Typography>Select a payroll run to view its journal entries</Typography>
            </Box>
          )}

          {loadingJournal && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
          )}

          {journal && !loadingJournal && (
            <>
              {/* Header */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{journal.runName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {journal.entityName} · Period: {journal.periodStart} → {journal.periodEnd} · Pay date: {journal.payDate}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Generated: {new Date(journal.generatedAt).toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined" startIcon={<FileDownload />}
                      onClick={() => downloadCsv(journal.runId, 'detail', `payroll-journal-${journal.runName}-detail.csv`)}>
                      Detail CSV
                    </Button>
                    <Button size="small" variant="contained" startIcon={<FileDownload />}
                      onClick={() => downloadCsv(journal.runId, 'summary', `payroll-journal-${journal.runName}-summary.csv`)}
                      sx={{ bgcolor: '#1b5e20' }}>
                      Summary CSV
                    </Button>
                  </Box>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Grid container spacing={2}>
                  {[
                    { label: 'Total Gross',      value: fmt(journal.totalGross, journal.currency),      color: '#2e7d32' },
                    { label: 'Total Deductions', value: fmt(journal.totalDeductions, journal.currency),  color: '#b71c1c' },
                    { label: 'Total Net Pay',    value: fmt(journal.totalNet, journal.currency),         color: '#1565c0' },
                    { label: 'Journal Balance',  value: balanced ? '✅ Balanced' : '❌ Unbalanced',       color: balanced ? '#2e7d32' : '#b71c1c' },
                  ].map(s => (
                    <Grid size={{ xs: 6, sm: 3 }} key={s.label}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={700} color={s.color}>{s.value}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              {!balanced && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Journal is not balanced — total debits ({fmt(debitTotal, journal.currency)}) ≠ total credits ({fmt(creditTotal, journal.currency)}). Check payroll items for missing data.
                </Alert>
              )}

              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Journal Lines" />
                <Tab label="GL Account Summary" />
              </Tabs>

              {/* ── Journal Lines ── */}
              {tab === 0 && (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="right" sx={{ width: 50 }}>#</TableCell>
                        <TableCell>Account</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Memo</TableCell>
                        <TableCell align="right" sx={{ color: '#2e7d32' }}>Debit</TableCell>
                        <TableCell align="right" sx={{ color: '#b71c1c' }}>Credit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {journal.lines.map((l: any) => (
                        <TableRow key={l.lineNo} hover
                          sx={{ bgcolor: l.itemType === 'net_pay' ? '#e8f5e920' : undefined }}>
                          <TableCell align="right" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>{l.lineNo}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip size="small" label={l.accountCode}
                                sx={{ bgcolor: accountColor(l.accountCode) + '15', color: accountColor(l.accountCode), fontSize: '0.6rem', fontFamily: 'monospace', minWidth: 36 }} />
                              <Typography variant="body2">{l.accountName}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell><Typography variant="caption" color="text.secondary">{l.department}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{l.memo}</Typography></TableCell>
                          <TableCell align="right">
                            {l.side === 'debit' ? (
                              <Typography variant="body2" fontWeight={600} color="#2e7d32">
                                {l.amount.toLocaleString('en-HK', { minimumFractionDigits: 2 })}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="right">
                            {l.side === 'credit' ? (
                              <Typography variant="body2" fontWeight={600} color="#b71c1c">
                                {l.amount.toLocaleString('en-HK', { minimumFractionDigits: 2 })}
                              </Typography>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Totals row */}
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell colSpan={4} align="right" sx={{ fontWeight: 700 }}>TOTALS</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: '#2e7d32' }}>
                          {debitTotal.toLocaleString('en-HK', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: '#b71c1c' }}>
                          {creditTotal.toLocaleString('en-HK', { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* ── GL Account Summary ── */}
              {tab === 1 && (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Account</TableCell>
                        <TableCell align="right" sx={{ color: '#2e7d32' }}>Debit</TableCell>
                        <TableCell align="right" sx={{ color: '#b71c1c' }}>Credit</TableCell>
                        <TableCell align="right">Net</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {journal.summary.map((s: any) => {
                        const net = s.debit - s.credit;
                        return (
                          <TableRow key={s.code} hover>
                            <TableCell>
                              <Chip size="small" label={s.code}
                                sx={{ bgcolor: accountColor(s.code) + '15', color: accountColor(s.code), fontSize: '0.65rem', fontFamily: 'monospace' }} />
                            </TableCell>
                            <TableCell><Typography variant="body2">{s.account}</Typography></TableCell>
                            <TableCell align="right">
                              {s.debit > 0 ? (
                                <Typography variant="body2" fontWeight={600} color="#2e7d32">
                                  {s.debit.toLocaleString('en-HK', { minimumFractionDigits: 2 })}
                                </Typography>
                              ) : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {s.credit > 0 ? (
                                <Typography variant="body2" fontWeight={600} color="#b71c1c">
                                  {s.credit.toLocaleString('en-HK', { minimumFractionDigits: 2 })}
                                </Typography>
                              ) : '—'}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={700}
                                color={net > 0 ? '#2e7d32' : net < 0 ? '#b71c1c' : 'text.secondary'}>
                                {net !== 0 ? `${net > 0 ? '+' : ''}${net.toLocaleString('en-HK', { minimumFractionDigits: 2 })}` : '0.00'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Balance check row */}
                      <TableRow sx={{ bgcolor: balanced ? '#e8f5e9' : '#ffebee' }}>
                        <TableCell colSpan={2} align="right" sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: '#2e7d32' }}>
                          {debitTotal.toLocaleString('en-HK', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: '#b71c1c' }}>
                          {creditTotal.toLocaleString('en-HK', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell align="right">
                          <Chip size="small"
                            label={balanced ? '✅ Balanced' : `❌ Diff: ${(debitTotal - creditTotal).toFixed(2)}`}
                            sx={{ bgcolor: balanced ? '#2e7d32' : '#b71c1c', color: '#fff', fontSize: '0.65rem' }} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
